'use client'

import { useRef, type ReactNode, Fragment, MouseEvent } from 'react'
import { motion, useInView } from 'motion/react'
import Link from '@/components/ui/Link'
import Pill from '@/components/ui/Pill'
import { Icon } from '@/icons/Icon'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn, scrollToSection } from '@/lib/utils'

const titleContainer = cva('flex', {
  variants: {
    theme: {
      light: 'border-gray-200 dark:border-white/20',
      dark: 'border-white/15',
    },
    type: {
      primary: 'mx-auto flex-col gap-6',
      split: 'mx-auto flex-col gap-6',
      secondary: 'mx-auto flex-col gap-3',
      default: 'justify-between border-b w-[calc(100%-16px)] mx-2',
    },
    fullWidth: {
      true: 'py-2.5 md:py-5',
      false: 'py-2.5 md:py-4',
    },
    align: {
      center: 'items-center text-center',
      left: 'items-start',
      right: 'items-end text-right',
    },
  },
  defaultVariants: {
    theme: 'light',
    type: 'default',
    fullWidth: false,
    align: 'center',
  },
  compoundVariants: [
    {
      type: 'default',
      fullWidth: true,
      align: 'center',
    },
    {
      type: 'default',
      fullWidth: false,
      align: 'right',
    },
  ],
})

const titleText = cva('leading-normal', {
  variants: {
    theme: {
      light: 'text-gray-950 dark:text-white',
      dark: 'text-white',
    },
    type: {
      primary: 'font-heading font-bold text-balance leading-tight text-36 md:text-48 bg-flash',
      split:
        'font-heading font-bold leading-tight text-36 bg-flash [&>p]:text-black/30 dark:[&>p]:text-white/30 [&>p]:font-normal',
      secondary: 'font-heading font-bold text-balance text-[1.25rem] md:text-[1.5rem]',
      default: 'font-semibold truncate text-[1rem]',
    },
    fullWidth: {
      true: '',
      false: '',
    },
    first: {
      true: '',
      false: '',
    },
  },
  defaultVariants: {
    theme: 'light',
    fullWidth: false,
    type: 'default',
  },
  compoundVariants: [
    {
      type: 'primary',
      first: true,
      className: 'lg:text-64',
    },
  ],
})

const paragraphText = cva(
  'text-balance [&>p>span]:font-semibold [&>p>span]:text-black dark:[&>p>span]:text-white',
  {
    variants: {
      theme: {
        light: 'text-gray-600 dark:text-white/60',
        dark: 'text-white/60',
      },
      type: {
        primary: 'text-18 lg:text-20',
        split: 'text-18 lg:text-20',
        secondary: 'text-18 md:text-16',
        default: 'text-18 lg:text-20',
      },
    },
    defaultVariants: {
      theme: 'light',
    },
  },
)

const subtitleText = cva('flex items-center gap-3 text-gray-400', {
  variants: {
    type: {
      primary: 'text-14 text-primary-500 font-medium',
      split: 'text-14 text-primary-500 font-medium',
      secondary: 'text-14 text-primary-500 font-medium',
      default: 'text-13',
    },
  },
  defaultVariants: {
    type: 'default',
  },
})

const buttonVariants = cva('', {
  variants: {
    theme: {
      light: '',
      dark: '',
    },
    type: {
      primary: '',
      split: '',
      secondary:
        'h-7 whitespace-nowrap px-3 text-14 font-medium text-primary-500 transition-colors hover:text-gray-950',
      default:
        'h-7 whitespace-nowrap px-3.5 text-13 text-primary-500 transition-colors hover:text-gray-950',
    },
  },
  defaultVariants: {
    theme: 'light',
    type: 'default',
  },
  compoundVariants: [
    {
      type: 'default',
      theme: 'dark',
      className: 'hover:text-white',
    },
  ],
})

type ContentType = string | ReactNode

export type TitleProps = {
  title: ContentType
  split?: ContentType
  subtitle?: ContentType
  icon?: ReactNode
  paragraphs?: ContentType[]
  buttons?: {
    label: string
    link?: string
    click?: () => void
  }[]
  fullWidth?: boolean
  className?: string
  theme?: 'light' | 'dark'
  type?: 'primary' | 'split' | 'secondary' | 'default'
  align?: 'center' | 'left' | 'right'
  maxWidth?: number
  first?: boolean
  tag?: 'h1' | 'h2' | 'h3' | 'h4' | 'span' | 'div'
  children?: ReactNode
} & VariantProps<typeof titleContainer>

export default function Title({
  title,
  split,
  subtitle,
  icon,
  paragraphs,
  buttons,
  fullWidth,
  className,
  theme = 'light',
  type,
  align,
  maxWidth,
  first,
  tag,
  children,
}: TitleProps) {
  const Tag = tag ?? (first ? 'h1' : 'h2')
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true })

  const createSpanAnimations = (content: string) => {
    const parts = content.split(/(<span[^>]*>.*?<\/span>)/g)
    return parts.map((part, index) => {
      if (part.startsWith('<span')) {
        const classMatch = part.match(/class=['"]([^'"]*)['"]/)
        const isDark = classMatch?.[1]?.includes('dark')
        const spanContent = part.replace(/<\/?span[^>]*>/g, '')
        return (
          <motion.span
            key={index}
            initial={{ backgroundPosition: '100% center' }}
            animate={
              isInView
                ? {
                    backgroundPosition: '-100% center',
                    transition: {
                      delay: index * 0.3,
                      duration: 1,
                      ease: 'easeInOut',
                    },
                  }
                : {}
            }
            className={`bg-flash ${isDark ? 'dark' : ''}`}
          >
            {spanContent}
          </motion.span>
        )
      }

      if (part.includes('<br />')) {
        const firstPart = part.split('<br />')[0]
        const secondPart = part.split('<br />')[1]
        const newPart = (
          <Fragment key={index}>
            {firstPart}
            <br />
            {secondPart}
          </Fragment>
        )
        return newPart
      }

      return part
    })
  }

  function handleHashClick(e: MouseEvent<HTMLAnchorElement>, hash: string) {
    e.preventDefault()
    const id = hash.substring(1)
    scrollToSection({ id, offset: 120, animate: true, hideHash: true })
  }

  const renderButtons = () => (
    <div className="flex flex-col items-center gap-2.5 sm:flex-row">
      {buttons?.map((btn, index) => {
        const buttonText = btn.label ?? 'See all'
        const isHashLink = btn.link?.startsWith('#')

        const primaryButton = (
          <Pill
            size="large"
            theme={theme}
            shadow={index === 0}
            style={index === 0 ? 'default' : 'outline'}
          >
            {buttonText}
          </Pill>
        )

        const secondaryButton = (
          <div className="flex h-full items-center gap-2">
            {buttonText}
            <span className="rotate-180">
              <Icon name="chevron-left" size={12} />
            </span>
          </div>
        )

        return (
          <Fragment key={index}>
            {btn.link ? (
              <Link
                href={btn.link}
                theme="primary"
                className={buttonVariants({ theme, type })}
                onClick={isHashLink ? (e) => handleHashClick(e, btn.link!) : undefined}
              >
                {type === 'primary'
                  ? primaryButton
                  : type === 'secondary'
                    ? secondaryButton
                    : buttonText}
              </Link>
            ) : btn.click ? (
              <button onClick={btn.click} className={buttonVariants({ theme, type })}>
                {type === 'primary'
                  ? primaryButton
                  : type === 'secondary'
                    ? secondaryButton
                    : buttonText}
              </button>
            ) : null}
          </Fragment>
        )
      })}
    </div>
  )

  return (
    <div
      className={cn(titleContainer({ theme, type, align, fullWidth }), className)}
      style={{
        maxWidth: maxWidth
          ? `${maxWidth}px`
          : type === 'primary' || type === 'split'
            ? '740px'
            : undefined,
      }}
    >
      {type === 'primary' || type === 'split' || type === 'secondary' ? (
        <>
          {subtitle || icon ? (
            <div className={subtitleText({ type })}>
              {icon && icon}
              {subtitle && <span className={subtitleText({ type })}>{subtitle}</span>}
            </div>
          ) : null}
          <Tag ref={ref} className={titleText({ theme, type, first })}>
            {typeof title === 'string' ? createSpanAnimations(title) : title}
            {split && <p>{split}</p>}
          </Tag>
          {paragraphs && (
            <div className={paragraphText({ theme, type })}>
              {paragraphs.map((paragraph, index) => (
                <Fragment key={index}>
                  {typeof paragraph === 'string' ? (
                    <p dangerouslySetInnerHTML={{ __html: paragraph }} className="text-balance" />
                  ) : (
                    <p className="text-balance">{paragraph}</p>
                  )}
                </Fragment>
              ))}
            </div>
          )}
          {buttons && renderButtons()}
        </>
      ) : (
        <>
          <div className="flex flex-col gap-1 truncate">
            {subtitle && <span className={subtitleText()}>{subtitle}</span>}
            <Tag
              className={titleText({ theme, fullWidth })}
              {...(typeof title === 'string'
                ? { dangerouslySetInnerHTML: { __html: title } }
                : { children: title })}
            />
          </div>
          {buttons && renderButtons()}
          {children}
        </>
      )}
    </div>
  )
}
